import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import L, { CircleMarker, Polygon } from 'leaflet'
import {
    activeGeometryIndexAtom,
    activeGeometryToolAtom,
    setActiveGeometryToolAtom,
} from '@/js/editor/state/editorUiAtoms.ts'
import {
    activePoiIdAtom,
    addPoiGeometryAtom,
    removePoiGeometryAtom,
    updatePoiGeometryAtom,
} from '@/js/editor/state/poiAtoms.ts'
import { GeometryObject } from 'geojson'
import { useRegisteredLayer } from '@/js/editor/components/map/LayerRegistryContext.tsx'

export type GeometryActions = 'move' | 'edit' | 'rotate'

type EditableLayer = CircleMarker | Polygon

const isEditableLayer = (layer: L.Layer | null): layer is EditableLayer => {
    return layer instanceof CircleMarker || layer instanceof Polygon
}
const disableLayerTools = (layer: EditableLayer) => {
    layer.pm.disable()
    layer.pm.disableLayerDrag()
    layer.pm.disableRotate()
}

export const useDisableActiveTool = () => {
    const setActiveGeometryTool = useSetAtom(setActiveGeometryToolAtom)
    const activePoiId = useAtomValue(activePoiIdAtom)
    const activeGeometryIndex = useAtomValue(activeGeometryIndexAtom)
    const activeLayer = useRegisteredLayer(activePoiId, activeGeometryIndex)

    return useCallback(() => {
        setActiveGeometryTool(null)

        if (isEditableLayer(activeLayer)) {
            disableLayerTools(activeLayer)
        }
    }, [activeLayer, setActiveGeometryTool])
}

export const useControls = () => {
    const activePoiId = useAtomValue(activePoiIdAtom)
    const activeGeometryIndex = useAtomValue(activeGeometryIndexAtom)
    const activeGeometryTool = useAtomValue(activeGeometryToolAtom)
    const activeLayer = useRegisteredLayer(activePoiId, activeGeometryIndex)

    const setActiveGeometryTool = useSetAtom(setActiveGeometryToolAtom)
    const addPoiGeometry = useSetAtom(addPoiGeometryAtom)
    const updatePoiGeometry = useSetAtom(updatePoiGeometryAtom)
    const removePoiGeometry = useSetAtom(removePoiGeometryAtom)

    const disableActiveTool = useDisableActiveTool()

    useEffect(() => {
        if (!isEditableLayer(activeLayer)) {
            return
        }

        disableLayerTools(activeLayer)

        if (activeGeometryTool === 'move') {
            activeLayer.pm.enableLayerDrag()
        }

        if (activeGeometryTool === 'rotate' && activeLayer instanceof Polygon) {
            activeLayer.pm.enableRotate()
        }

        if (activeGeometryTool === 'edit' && activeLayer instanceof Polygon) {
            activeLayer.pm.enable()
        }

        return () => {
            disableLayerTools(activeLayer)
        }
    }, [activeGeometryTool, activeLayer])

    const handleUpdate = useCallback(() => {
        if (!activeLayer || activeGeometryIndex === null) {
            return
        }

        // Leaflet Geoman adds toGeoJSON to supported layers.
        // @ts-expect-error Geoman extension is not included in Leaflet's types.
        const geoJson = activeLayer.toGeoJSON()

        updatePoiGeometry({
            index: activeGeometryIndex,
            geometry: geoJson.geometry as GeometryObject,
        })
    }, [activeGeometryIndex, activeLayer, updatePoiGeometry])

    useEffect(() => {
        if (!activeLayer) {
            return
        }

        const events = ['pm:dragend', 'pm:markerdragend', 'pm:vertexadded', 'pm:vertexremoved', 'pm:rotateend']

        events.forEach((event) => {
            activeLayer.addEventListener(event, handleUpdate)
        })

        return () => {
            events.forEach((event) => {
                activeLayer.removeEventListener(event, handleUpdate)
            })
        }
    }, [activeLayer, handleUpdate])

    const toggleTool = useCallback(
        (tool: GeometryActions) => {
            if (activeGeometryTool === tool) {
                disableActiveTool()

                return
            }

            setActiveGeometryTool(tool)
        },
        [activeGeometryTool, disableActiveTool, setActiveGeometryTool]
    )

    const updateActiveGeometry = useCallback(
        (geometry: GeometryObject) => {
            if (activeGeometryIndex === null) {
                return
            }

            updatePoiGeometry({
                index: activeGeometryIndex,
                geometry,
            })
        },
        [activeGeometryIndex, updatePoiGeometry]
    )

    const removeActiveGeometry = useCallback(() => {
        if (activeGeometryIndex === null) {
            return
        }

        removePoiGeometry(activeGeometryIndex)
        disableActiveTool()
    }, [activeGeometryIndex, disableActiveTool, removePoiGeometry])

    return {
        tool: activeGeometryTool,
        setTool: toggleTool,
        addGeometry: addPoiGeometry,
        updateActiveGeometry,
        removeActiveGeometry,
    }
}
